"use client";

import { Button, Modal, Rating, Textarea } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

import { trackReview } from "../../utils/reviewEvent";

interface TrustpilotFeedbackModalProps {
  opened: boolean;
  onClose: () => void;
  applicationId: string;
  trustpilotWebsiteId: string;
  domain: string;
  submitFeedback: (payload: {
    rate: number;
    details: string;
    websiteId: string;
    applicationId: string;
  }) => Promise<{ success: boolean }>;
  getTrustpilotInvitationLink: (
    applicationId: string,
    redirectURI: string,
  ) => Promise<{ success: boolean; invitationLink?: string }>;
  /** Replaces the generic heading, e.g. a service's own success message. */
  title?: string;
  /** Lowest rating sent on to Trustpilot; below it the customer stays in-app. */
  redirectFrom?: number;
  /** Lets the customer close it without rating. */
  dismissible?: boolean;
  /** The Trustpilot review page was opened for the customer. */
  onRedirected?: () => void;
}

export function TrustpilotFeedbackModal({
  opened,
  onClose,
  applicationId,
  trustpilotWebsiteId,
  domain,
  submitFeedback,
  getTrustpilotInvitationLink,
  title,
  redirectFrom = 3,
  dismissible = false,
  onRedirected,
}: TrustpilotFeedbackModalProps) {
  const t = useTranslations("dashboard");
  const [rating, setRating] = useState(-1);
  const [feedback, setFeedback] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (opened) {
      trackReview(applicationId, { surface: "popup", step: "shown" });
      setRating(-1);
      setFeedback("");
      setIsLoading(false);
      setSubmitted(false);
    }
  }, [opened, applicationId]);

  /** Only for a rating we failed to store — that is what the message says. A
   * missing invitation link is a separate, best-effort failure: showing "we
   * could not record your rating" for it tells the customer something untrue
   * and sends whoever debugs it after the wrong half of the flow. The link
   * failure is logged server-side by the action instead. */
  const notifyFailure = () =>
    notifications.show({
      title: t("feedback.error-title"),
      message: t("feedback.error-message"),
      color: "red",
    });

  const handleFeedbackSubmit = async (stars?: number): Promise<boolean> => {
    try {
      setIsLoading(true);
      const { success } = await submitFeedback({
        rate: stars ?? rating,
        details: feedback,
        websiteId: trustpilotWebsiteId,
        applicationId,
      });

      if (success) setFeedback("");

      return success;
    } catch {
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleRating = async (stars: number) => {
    setRating(stars);
    // Mantine's empty value (0) can be picked by keyboard: it is no rating.
    if (stars < 1) return;
    trackReview(applicationId, {
      surface: "popup",
      step: "rated",
      rating: stars,
    });

    if (stars < redirectFrom) {
      trackReview(applicationId, { surface: "popup", step: "kept_internal" });
      return;
    }

    try {
      if (applicationId) {
        const result = await getTrustpilotInvitationLink(
          applicationId,
          domain,
        ).catch(() => ({ success: false, invitationLink: undefined }));

        if (result.success && result.invitationLink) {
          // No "noreferrer": with it `window.open` always returns null, and a
          // tab the browser blocked must not count as sent. The opener is cut
          // by hand instead; both apps' `Referrer-Policy` header sends only our
          // origin cross-site, never the magic link's `?token=`.
          const tab = window.open(result.invitationLink, "_blank");
          if (tab) {
            tab.opener = null;
            trackReview(applicationId, {
              surface: "popup",
              step: "sent_to_trustpilot",
            });
            onRedirected?.();
          }
        }
      }

      if (!(await handleFeedbackSubmit(stars))) notifyFailure();
    } finally {
      setSubmitted(true);
    }
  };

  const handleLowRatingSubmit = async () => {
    try {
      if (!(await handleFeedbackSubmit())) notifyFailure();
    } finally {
      onClose();
    }
  };

  return (
    <Modal
      centered
      zIndex={1000}
      classNames={{
        overlay: "backdrop-blur-xs bg-black/40",
        header: "items-start",
        title: "font-medium",
        body: "flex flex-col gap-3 py-0",
        content: "px-5 py-10 md:p-10",
      }}
      /* No-op unless dismissible: close is otherwise controlled by the parent via onClose */
      onClose={dismissible ? onClose : () => {}}
      withCloseButton={dismissible}
      closeButtonProps={{ "aria-label": t("feedback.close") }}
      closeOnClickOutside={dismissible}
      closeOnEscape={dismissible}
      opened={opened}
      size="md"
      title={
        <span
          data-testid="feedback-modal-title"
          className="text-3xl font-semibold text-primary"
        >
          {title ?? t("feedback.title")}
        </span>
      }
    >
      <p className="text-lg">{t("feedback.rate-experience")}</p>
      <Rating
        onChange={submitted ? undefined : handleRating}
        readOnly={submitted}
        size="lg"
        value={rating}
        color="yellow"
        classNames={{ label: "outline-0!" }}
        attributes={{ label: { "data-testid": "feedback-modal-star" } }}
      />

      {rating > 0 && rating < redirectFrom && (
        <>
          <Textarea
            label={t("feedback.tell-more")}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder={t("feedback.describe-issue")}
            rows={5}
            data-testid="feedback-modal-details"
            value={feedback}
          />
          <Button
            className="ml-auto"
            loading={isLoading}
            onClick={handleLowRatingSubmit}
            data-testid="feedback-modal-submit"
          >
            {t("feedback.cta")}
          </Button>
        </>
      )}

      {submitted && (
        <Button
          className="ml-auto"
          onClick={onClose}
          data-testid="feedback-modal-close"
        >
          {t("feedback.close")}
        </Button>
      )}
    </Modal>
  );
}
